import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, RefreshCw } from "lucide-react";
import { api } from "../api";
import type { RunRecord, RunStatus } from "../../shared/types";

interface RunsViewProps {
  onOpenWorkflow: (id: string) => void;
}

const statusLabels: Record<RunStatus, string> = {
  queued: "Na fila",
  running: "Executando",
  waiting_approval: "Aguardando",
  completed: "Concluída",
  failed: "Falhou",
  cancelled: "Cancelada",
};

function duration(run: RunRecord) {
  if (!run.startedAt) return "-";
  const end = run.finishedAt ? new Date(run.finishedAt).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((end - new Date(run.startedAt).getTime()) / 1000));
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function RunsView({ onOpenWorkflow }: RunsViewProps) {
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [filter, setFilter] = useState<"all" | RunStatus>("all");
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const result = await api.runs();
      setRuns(result.runs);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);
  const visible = useMemo(() => filter === "all" ? runs : runs.filter((run) => run.status === filter), [runs, filter]);

  return (
    <main className="workspace-page">
      <header className="page-header">
        <div><p className="eyebrow">Observabilidade</p><h1>Execuções</h1><p>{runs.length} registros recentes</p></div>
        <div className="page-actions"><select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}><option value="all">Todos os estados</option><option value="running">Executando</option><option value="waiting_approval">Aguardando aprovação</option><option value="completed">Concluídas</option><option value="failed">Falhas</option></select><button className="icon-button" title="Atualizar" aria-label="Atualizar" onClick={() => void load()}><RefreshCw className={loading ? "spin" : ""} size={16} /></button></div>
      </header>
      <section className="data-table runs-table" aria-label="Execuções">
        <div className="data-table__head"><span>Workflow</span><span>Estado</span><span>Gatilho</span><span>Duração</span><span>Custo</span><span>Início</span><span /></div>
        {visible.map((run) => (
          <div className="data-table__row" key={run.id}>
            <button className="table-primary" onClick={() => onOpenWorkflow(run.workflowId)}><span className={`run-state run-state--${run.status}`} /><span><strong>{run.workflowName ?? "Workflow"}</strong><small>#{run.id.slice(0, 8)}</small></span></button>
            <span className={`status-badge status-badge--${run.status}`}>{statusLabels[run.status]}</span>
            <span>{run.triggerType === "manual" ? "Manual" : "Webhook"}</span>
            <span>{duration(run)}</span>
            <span>R$ {run.cost.toFixed(3).replace(".", ",")}</span>
            <time>{new Date(run.createdAt).toLocaleString("pt-BR")}</time>
            <button className="icon-button" title="Abrir workflow" aria-label="Abrir workflow" onClick={() => onOpenWorkflow(run.workflowId)}><ArrowUpRight size={16} /></button>
          </div>
        ))}
        {visible.length === 0 && <div className="table-empty">Nenhuma execução neste filtro.</div>}
      </section>
    </main>
  );
}
